"use client";

import { useState } from "react";
import { SmokeGraph } from "@/components/charts/smoke-graph";
import type { SmokeData } from "@/lib/api";

/**
 * Generates realistic synthetic monitoring data with visible
 * packet loss events so visitors see the classic SmokePing
 * green → blue → violet color shift in action.
 */
function generateDemoData(): SmokeData {
    const now = Math.floor(Date.now() / 1000);
    const start = now - 3600; // 1 hour
    const step = 30; // 30s intervals
    const points = Math.floor((now - start) / step);

    const percentiles: Record<string, [number, number][]> = {
        p50: [], p75: [], p90: [], p95: [], p99: [],
    };
    const packetLoss: [number, number][] = [];

    let baseLatency = 12; // ms

    // Plan two visible loss events so the color shift is obvious
    // Event 1: ~15-25 min in, moderate loss (5-15%) → blue/indigo smoke
    // Event 2: ~40-50 min in, heavier loss (15-35%) → violet/purple smoke
    const event1Start = Math.floor(points * 0.2);
    const event1End = Math.floor(points * 0.28);
    const event2Start = Math.floor(points * 0.55);
    const event2End = Math.floor(points * 0.65);

    for (let i = 0; i < points; i++) {
        const ts = start + i * step;
        const phase = (i / points) * Math.PI * 4;

        // Gentle wave with occasional micro-spikes
        const wave = Math.sin(phase) * 3 + Math.sin(phase * 2.7) * 1.5;
        const spike = Math.random() < 0.03 ? Math.random() * 15 : 0;
        const jitter = (Math.random() - 0.5) * 2;

        const p50ms = Math.max(2, baseLatency + wave + jitter + spike);

        // More jitter during loss events (realistic — loss correlates with instability)
        const inEvent1 = i >= event1Start && i <= event1End;
        const inEvent2 = i >= event2Start && i <= event2End;
        const eventJitter = inEvent1 ? 1.8 : inEvent2 ? 2.5 : 1;
        const spread = (1 + Math.random() * 0.5) * eventJitter;

        percentiles.p50.push([ts, p50ms / 1000]);
        percentiles.p75.push([ts, (p50ms + 2 * spread) / 1000]);
        percentiles.p90.push([ts, (p50ms + 5 * spread) / 1000]);
        percentiles.p95.push([ts, (p50ms + 9 * spread) / 1000]);
        percentiles.p99.push([ts, (p50ms + 16 * spread) / 1000]);

        // Packet loss
        let loss = 0;
        if (inEvent1) {
            // Moderate event: 5-15% loss, ramping up and down
            const eventProgress = (i - event1Start) / (event1End - event1Start);
            const envelope = Math.sin(eventProgress * Math.PI); // bell curve
            loss = 5 + envelope * 10 + Math.random() * 3;
        } else if (inEvent2) {
            // Heavier event: 15-35% loss
            const eventProgress = (i - event2Start) / (event2End - event2Start);
            const envelope = Math.sin(eventProgress * Math.PI);
            loss = 15 + envelope * 20 + Math.random() * 5;
        } else {
            // Background: rare tiny loss
            loss = Math.random() < 0.03 ? Math.random() * 2 : 0;
        }
        packetLoss.push([ts, loss]);

        // Slowly drift the base latency
        baseLatency += (Math.random() - 0.5) * 0.3;
        baseLatency = Math.max(8, Math.min(20, baseLatency));
    }

    return {
        target: "gateway.eu-west-1",
        probe_type: "icmp",
        percentiles,
        packet_loss: packetLoss,
        stats: {
            current_latency_ms: baseLatency,
            avg_latency_ms: 12.4,
            p95_latency_ms: 22.1,
            avg_packet_loss: 1.8,
        },
        time_range: { start, end: now, step },
    };
}

export function DemoSmokeGraph() {
    const [data] = useState<SmokeData>(() => generateDemoData());

    return (
        <SmokeGraph
            data={data}
            height={280}
            compact={false}
            showLegend
        />
    );
}
